import { Array as RuntypeArray } from 'runtypes';

import { IdoNotificationSubscription, IdoNotificationSubscriptionRunType } from 'src/models/userInterfaceSettings';

const IdoNotificationSubscriptionListRunType = RuntypeArray(IdoNotificationSubscriptionRunType);

export function parseIdoNotificationSubscriptions(rawValue: string | undefined): IdoNotificationSubscription[] {
	if (rawValue === undefined || rawValue === '') {
		return [];
	}

	try {
		const parsedValue: unknown = JSON.parse(rawValue);
		return IdoNotificationSubscriptionListRunType.check(parsedValue);
	} catch {
		return [];
	}
}

export function serializeIdoNotificationSubscriptions(subscriptions: IdoNotificationSubscription[]): string {
	return JSON.stringify(subscriptions);
}

export function subscriptionsAreEqual(
	first: IdoNotificationSubscription,
	second: IdoNotificationSubscription
): boolean {
	return (
		first.instanceId === second.instanceId &&
		first.savedByUserId === second.savedByUserId &&
		first.source === second.source
	);
}

export function containsSubscription(
	subscriptions: IdoNotificationSubscription[],
	candidate: IdoNotificationSubscription
): boolean {
	return subscriptions.some((subscription) => subscriptionsAreEqual(subscription, candidate));
}

export function savedByUserIdsWithAllInstances(subscriptions: IdoNotificationSubscription[]): number[] {
	const savedByUserIds = new Set<number>();

	subscriptions.forEach((subscription) => {
		if (subscription.source === 'all') {
			savedByUserIds.add(subscription.savedByUserId);
		}
	});

	return Array.from(savedByUserIds);
}

export function materializeAllInstanceSubscriptions(
	subscriptions: IdoNotificationSubscription[],
	accessibleInstanceIds: string[]
): IdoNotificationSubscription[] {
	const rematerialized: IdoNotificationSubscription[] = subscriptions.filter(
		(subscription) => subscription.source === 'explicit'
	);

	savedByUserIdsWithAllInstances(subscriptions).forEach((savedByUserId) => {
		accessibleInstanceIds.forEach((instanceId) => {
			const candidate: IdoNotificationSubscription = { instanceId, savedByUserId, source: 'all' };

			if (!containsSubscription(rematerialized, candidate)) {
				rematerialized.push(candidate);
			}
		});
	});

	return rematerialized;
}

interface IdoNotificationSubscriptionGroup {
	allInstances: boolean;
	instanceId: null | string;
	savedByUserId: number;
}

export function groupIdoNotificationSubscriptions(
	subscriptions: IdoNotificationSubscription[]
): IdoNotificationSubscriptionGroup[] {
	const groups: IdoNotificationSubscriptionGroup[] = [];
	const seenAllInstanceGroups = new Set<number>();

	subscriptions.forEach((subscription) => {
		if (subscription.source === 'all') {
			if (!seenAllInstanceGroups.has(subscription.savedByUserId)) {
				seenAllInstanceGroups.add(subscription.savedByUserId);
				groups.push({ allInstances: true, instanceId: null, savedByUserId: subscription.savedByUserId });
			}

			return;
		}

		groups.push({
			allInstances: false,
			instanceId: subscription.instanceId,
			savedByUserId: subscription.savedByUserId,
		});
	});

	return groups;
}
